import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Grid,
    TextField,
    makeStyles,
    Typography,
    FormControlLabel,
    Checkbox,
} from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Editor from "ckeditor5-custom-build/build/ckeditor";
import apiHelper from "../../../util/ApiHelper";
import { toast } from "react-toastify";
import editorConfiguration from "../../../util/EditorConfiguration";
import { useLoading } from "../../../util/LoadingProvider";
import UploadImage from "../UploadImage";
import styled from "styled-components";
import {useHistory} from "react-router-dom";

const schema = yup.object().shape({
    nameEn: yup.string().required('Name (En) is required'),
    nameTc: yup.string().required('Name (Tc) is required'),
    price: yup.number().required('Price is required'),
    buyUrl: yup.string().required('Buy Url is required'),
});

const defaultFormValues = {
    imageEn: '',
    imageTc: '',
    priceEn: '',
    priceTc: '',
    nameEn: '',
    nameTc: '',
    descEn: '',
    descTc: '',
    buyUrl: '',
    status: 'INACTIVE',
    hkiffId: process.env.REACT_APP_HKIFF_ID_NO,
};

const useStyles = makeStyles((theme) => ({
    textInput: {
        width: "100%",
        marginBottom: "12px",

        "& input": {
            fontSize: "14px",
        },
    },
    content: {
        "& .ck-editor__editable_inline": {
            minHeight: "362px",
        },
    },
    buttonGroup: {
        marginTop: "40px",
        textAlign: "left",
        "& button": {
            color: "#306F9D",
            fontSize: "13px",
            textTransform: "inherit",
        },
    },
    box: {
        padding: 0,
    },
    input: {
        width: "100%",
    },
}));

const PriceTag = styled.span`
  color: #CCCCCC;
  transform: scale(0.8);
`;

export default function FestivalPassPage({ disabled, ...props }) {
    const { match } = props;
    const history = useHistory();
    const id = match.params.id;
    const [defaultValues, setDefaultValues] = useState(defaultFormValues);
    const { setLoading } = useLoading();

    const classes = useStyles();

    const {
        control,
        handleSubmit,
        getValues,
        setValue,
        reset,
    } = useForm({
        defaultValues: React.useMemo(() => defaultValues, [defaultValues]),
        resolver: yupResolver(schema),
    });

    const onSubmit = (value) => {
        if(id != "new") {
            updateFestival(value);
        }else {
            createFestival(value);
        }
    };

    const getFestival = React.useCallback(async () => {
        setLoading(true);
        try {
            const result = await apiHelper.hkiff.getFestivalPassById(id);
            console.log(result);
            const price = parseInt(result.data.priceEn.substring(1, result.data.priceEn.length));
            const data = { ...result.data, price }
            setDefaultValues(data);
            reset(data);
        } catch (err) {
            console.log(err);
        }
        setLoading(false);
    }, [id, setLoading]);

    const updateFestival = async (value) => {
        try {
            const { price, ...rest } = value;
            const data = { ...rest, priceEn: `$ ${price}`, priceTc: `$ ${price}` }
            const result = await apiHelper.hkiff.updateFestivalPass(data,id);
            if (result.data.id !== null || result.data.id !== undefined) {
                toast.dark("Your changes are saved.");
                getFestival();
            }
        } catch (err) {
            console.log(err);
        }
    };

    const createFestival = async (value) => {
        try {
            const { price, ...rest } = value;
            const data = { ...rest, priceEn: `$ ${price}`, priceTc: `$ ${price}` }
            const result = await apiHelper.hkiff.createFestivalPass(data);
            console.log(result);
            if (result.data.id !== null || result.data.id !== undefined) {
                history.push("/hkiff/ticket-and-faq/festival-pass/"+result.data.id);
                toast.dark("The Festival Pass is created.");
            }
        } catch (err) {
            console.log(err);
        }
    };

    useEffect(() => {
        if (id != "new") {
            getFestival();
        }
    }, [getFestival]);

    return (
        <div className={classes.content}>
            <Box width="100%">
                <Typography variant="h4" gutterBottom>
                    Ticket & FAQ &gt; Festival Pass
                </Typography>
            </Box>
            <form onSubmit={handleSubmit(onSubmit)}>
                <fieldset disabled={disabled} className="fieldset">
                    <Grid container xs={12} direction="row" spacing={3}>
                        <Grid item xs={12}>
                            <Controller
                                name="nameEn"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        label="Name (English)"
                                        variant="outlined"
                                        margin="dense"
                                        className={classes.textInput}
                                        fullWidth
                                        {...field}
                                        error={!!error}
                                        helperText={error ? error.message : "The name is how it appears on your site."}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Controller
                                name="nameTc"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        label="Name (Traditional Chinese)"
                                        variant="outlined"
                                        margin="dense"
                                        className={classes.textInput}
                                        fullWidth
                                        {...field}
                                        error={!!error}
                                        helperText={error ? error.message : "The name is how it appears on your site."}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Controller
                                name="price"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        label="Price"
                                        variant="outlined"
                                        margin="dense"
                                        className={classes.textInput}
                                        fullWidth
                                        {...field}
                                        error={!!error}
                                        helperText={error ? error.message : ""}
                                        type='number'
                                        InputProps={{
                                            startAdornment: (
                                                <PriceTag>HK$</PriceTag>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Controller
                                name="buyUrl"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        label="Buy Url"
                                        variant="outlined"
                                        margin="dense"
                                        className={classes.textInput}
                                        fullWidth
                                        {...field}
                                        error={!!error}
                                        helperText={error ? error.message : ""}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>

                    <Box
                        fontWeight={900}
                        fontSize={13}
                        mb={2}
                        style={{ marginTop: "30px" }}
                        width="100%"
                    >
                        <Typography variant="p">Description (English)</Typography>
                    </Box>

                    <Grid container xs={12} direction="row" spacing={3}>
                        <Grid item xs={12}>
                            <Controller
                                name="descEn"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <CKEditor
                                        editor={Editor}
                                        config={editorConfiguration}
                                        data={field.value}
                                        disabled={disabled}
                                        onReady={(editor) => {
                                            // You can store the "editor" and use when it is needed.
                                            console.log("Editor is ready to use!", editor);
                                            editor.setData(
                                                defaultValues.descEn
                                            );
                                        }}
                                        onChange={(event, editor) => {
                                            const data = editor.getData();
                                            field.onChange(data);
                                            // console.log({ event, editor, data });
                                        }}
                                        onBlur={(event, editor) => {
                                            console.log("Blur.", editor);
                                        }}
                                        onFocus={(event, editor) => {
                                            console.log("Focus.", editor);
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>

                    <Box fontWeight={900} fontSize={13} mt={4} mb={2} width="100%">
                        <Typography variant="p">
                            Description (Traditional Chinese)
                                </Typography>
                    </Box>

                    <Grid container xs={12} direction="row" spacing={3}>
                        <Grid item xs={12}>
                            <Controller
                                name="descTc"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <CKEditor
                                        editor={Editor}
                                        config={editorConfiguration}
                                        data={field.value}
                                        disabled={disabled}
                                        onReady={(editor) => {
                                            // You can store the "editor" and use when it is needed.
                                            console.log("Editor is ready to use!", editor);
                                            editor.setData(
                                                defaultValues.descTc
                                            );
                                        }}
                                        onChange={(event, editor) => {
                                            const data = editor.getData();
                                            field.onChange(data);
                                            // console.log({ event, editor, data });
                                        }}
                                        onBlur={(event, editor) => {
                                            console.log("Blur.", editor);
                                        }}
                                        onFocus={(event, editor) => {
                                            console.log("Focus.", editor);
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>

                    <Grid
                        container
                        item
                        xs={6}
                        direction="row"
                        spacing={3}
                        style={{ marginTop: "32px" }}
                    >
                        <Grid item xs={12}>
                            {defaultValues && (
                                <UploadImage
                                    name="imageEn"
                                    data={defaultValues}
                                    control={control}
                                    Controller={Controller}
                                    setValue={setValue}
                                    getValues={getValues}
                                    disabled={disabled}
                                    uploadLabel="Image (English)"
                                    changeLabel="Image (English)"
                                    uploadFolder="FESTIVAL_PASS"
                                />
                            )}
                        </Grid>
                    </Grid>

                    <Grid container item xs={6} direction="row" spacing={3}>
                        <Grid item xs={12}>
                            {defaultValues && (
                                <UploadImage
                                    name="imageTc"
                                    data={defaultValues}
                                    control={control}
                                    Controller={Controller}
                                    setValue={setValue}
                                    getValues={getValues}
                                    disabled={disabled}
                                    uploadLabel="Image (Traditional Chinese)"
                                    changeLabel="Image (Traditional Chinese)"
                                    uploadFolder="FESTIVAL_PASS"
                                />
                            )}
                        </Grid>
                    </Grid>

                    <Grid
                        container
                        item
                        xs={12}
                        spacing={3}
                        style={{ marginTop: "52px", marginLeft: 0 }}
                    >
                        <Grid item xs={3} style={{ paddingLeft: "4px" }}>
                            <Controller
                                control={control}
                                name="status"
                                render={({
                                    field: { value, onChange, ...rest },
                                    fieldState: { error },
                                }) => (
                                    <ToggleButtonGroup
                                        {...rest}
                                        value={value}
                                        onChange={(event, newFormats) => {
                                            onChange(newFormats);
                                        }}
                                        exclusive
                                        aria-label="Status"
                                        className={classes.input}
                                    >
                                        <ToggleButton
                                            value={"ACTIVE"}
                                            aria-label="Active"
                                            style={{ width: "50%" }}
                                            disabled={disabled}
                                        >
                                            Active
                                        </ToggleButton>
                                        <ToggleButton
                                            value={"INACTIVE"}
                                            aria-label="Inactive"
                                            style={{ width: "50%" }}
                                            disabled={disabled}
                                            // disabled={true} // hardcode now as inactive leads to server error
                                        >
                                            Inactive
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                )}
                            />
                        </Grid>
                    </Grid>

                    {disabled || (
                        <div className={classes.buttonGroup}>
                            <Button
                                type="submit"
                                variant="contained"
                                disableElevation
                                style={{
                                    color: "white",
                                    background: "#306F9D",
                                    marginRight: "5px",
                                    padding: "9px 16px",
                                }}
                            >
                                Save Changes
                            </Button>
                        </div>
                    )}
                </fieldset>
            </form>
        </div>
    );
}
